import { useCallback, useEffect } from 'react';
import { type ModalQueue, type ModalQueueEventPayload } from './useModalQueue';

interface CustomPopStateEvent extends PopStateEvent {
  state: { previousModalId?: number } | null;
}

type Options = {
  disable?: boolean;
  logToConsole?: boolean;
  nextJsBeforePopState?: (callback: () => boolean) => void;
};

const defaultOptions: Required<Options> = {
  disable: false,
  logToConsole: false,
  nextJsBeforePopState() {}
};

export default function useModalHistory(
  modalQueue: ModalQueue,
  opts?: Options
) {
  const options = { ...defaultOptions, ...opts };

  const logToConsole = useCallback(
    (...args: any[]) => {
      if (!options.logToConsole) return;

      console.debug(...args);
    },
    [options.logToConsole]
  );

  // Disable Next.js's pop state handler
  useEffect(() => {
    if (options.disable) return;

    options.nextJsBeforePopState.call(null, () => false);
  }, [options.disable, options.nextJsBeforePopState]);

  // Handle manually opening and closing modals
  useEffect(() => {
    if (options.disable) return;

    function onModalOpened(payload: ModalQueueEventPayload<'enqueued'>) {
      logToConsole('Modal opened, pushing new history state', {
        ...window.history.state,
        previousModalId: payload.id
      });

      // Add previousModalId to current history state so when we go back we
      // know which modal to close
      window.history.replaceState(
        { ...window.history.state, previousModalId: payload.id },
        '',
        window.location.href
      );

      window.history.pushState({}, '', window.location.href);
    }

    function onModalClosed(payload: ModalQueueEventPayload<'dequeued'>) {
      if (payload.goBackInHistory) {
        logToConsole('Modal closed, going back in history');

        window.history.back();
      } else {
        logToConsole('Modal closed, skipping going back in history');
      }
    }

    modalQueue.on('enqueued', onModalOpened);
    modalQueue.on('dequeued', onModalClosed);

    return () => {
      modalQueue.off('enqueued', onModalOpened);
      modalQueue.off('dequeued', onModalClosed);
    };
  }, [logToConsole, modalQueue, options.disable]);

  // Handle closing modals by navigation
  useEffect(() => {
    if (options.disable) return;

    function handlePopState(ev: CustomPopStateEvent) {
      const previousModalId = ev.state?.previousModalId;

      if (previousModalId != null && modalQueue.has(previousModalId)) {
        logToConsole('Traveled back in history, closing modal');

        modalQueue.dequeue(previousModalId, { goBackInHistory: false });
      }
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [logToConsole, modalQueue, options.disable]);
}
